import cn from 'classnames'
import * as R from 'ramda'
import s from './styles.module.css'
import Select, {
  components,
  SelectComponentsConfig,
  StylesConfig,
} from 'react-select'
import { useMediaQuery } from 'hooks/use-media-query'

export interface SelectOption {
  value: string
  label: string
}

interface GroupedOption {
  label: string
  options: SelectOption[]
}

export const MOST_COMMON_DIAGNOSES = [
  { code: 'E66.811', name: 'Obesity' },
  { code: 'E66.3', name: 'Overweight' },
  { code: 'R73.03', name: 'Pre-Diabetes' },
  { code: 'E11.9', name: 'Type 2 Diabetes' },
]

export const OBESITY_DIAGNOSES = [
  { code: 'E66.811', name: 'Obesity' },
  { code: 'E66.3', name: 'Overweight' },
]

export const DIABETES_DIAGNOSES = [
  { code: 'R73.03', name: 'Pre-Diabetes' },
  { code: 'E10.9', name: 'Type 1 Diabetes' },
  { code: 'E11.9', name: 'Type 2 Diabetes' },
  { code: 'O24.419', name: 'Gestational diabetes' },
]

export const CARDIOVASCULAR_DIAGNOSES = [
  { code: 'E78.5', name: 'High Cholesterol' },
  { code: 'I10', name: 'Hypertension' },
  { code: 'I25.10', name: 'Heart Disease' },
  { code: 'I13.0', name: 'Heart & Kidney Disease' },
]

export const KIDNEY_DIAGNOSES = [
  { code: 'N18.3', name: 'Moderate Kidney Disease, Stage 3' },
  { code: 'N18.4', name: 'Severe Kidney Disease, Stage 4' },
  { code: 'N18.5', name: 'Severe Kidney Disease, Stage 5' },
  { code: 'Z99.2', name: 'Kidney Disease on Dialysis' },
  { code: 'I13.0', name: 'Heart & Kidney Disease' },
]

export const GASTROINTESTINAL_DIAGNOSES = [
  { code: 'K58.9', name: 'IBS' },
  { code: 'K90.0', name: 'Celiac Disease' },
  { code: 'K21.9', name: 'GERD' },
]

export const EATING_DISORDERS_DIAGNOSES = [
  { code: 'F50.01', name: 'Anorexia, Restricting Type' },
  { code: 'F50.02', name: 'Anorexia, Binge-Eating/Purging Type' },
  { code: 'F50.2', name: 'Bulimia' },
]

export const ALLERGIES_DIAGNOSES = [
  { code: 'Z91.011', name: 'Diary Allergy' },
  { code: 'Z91.012', name: 'Egg Allergy' },
  { code: 'Z91.02', name: 'Food Additive Allergy' },
  { code: 'Z91.014', name: 'Meat Allergy' },
  { code: 'Z91.010', name: 'Peanut Allergy' },
  { code: 'Z91.013', name: 'Seafood Allergy' },
]

export const OTHER_DIAGNOSES = [
  { code: 'OTHER', name: 'Other Health Condition' },
]

export const ALL_DIAGNOSES_BY_CODE = R.indexBy(R.prop('code'), [
  ...OBESITY_DIAGNOSES,
  ...DIABETES_DIAGNOSES,
  ...CARDIOVASCULAR_DIAGNOSES,
  ...KIDNEY_DIAGNOSES,
  ...GASTROINTESTINAL_DIAGNOSES,
  ...EATING_DISORDERS_DIAGNOSES,
  ...ALLERGIES_DIAGNOSES,
  ...OTHER_DIAGNOSES,
])

const DIAGNOSIS_OPTIONS: GroupedOption[] = [
  {
    label: 'Most Common',
    options: MOST_COMMON_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Obesity',
    options: OBESITY_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Diabetes',
    options: DIABETES_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Cardiovascular',
    options: CARDIOVASCULAR_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Kidney Disease',
    options: KIDNEY_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Gastrointestinal Disorders',
    options: GASTROINTESTINAL_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Eating Disorders',
    options: EATING_DISORDERS_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Allergies',
    options: ALLERGIES_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
  {
    label: 'Others',
    options: OTHER_DIAGNOSES.map(diagnosis => ({
      value: diagnosis.code,
      label: diagnosis.name,
    })),
  },
]

const CustomOption = ({ children, ...props }: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div className={cn(s.checkbox, props.isSelected && s.selected)}>
          {props.isSelected && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13.3333 4L6 11.3333L2.66667 8"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        {children}
      </div>
    </components.Option>
  )
}

const DiagnosisSelect = ({
  value,
  onChange,
  placeholder = undefined,
  className = '',
  styleOverrides = {},
  componentsOverrides = {},
}: {
  value: SelectOption[]
  onChange: (selectedOptions: SelectOption[]) => void
  placeholder?: string
  className?: string
  styleOverrides?: StylesConfig
  componentsOverrides?: SelectComponentsConfig<
    SelectOption,
    boolean,
    GroupedOption
  >
}) => {
  const isMobile = useMediaQuery('(max-width: 425px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  return (
    <Select
      className={className}
      classNamePrefix="select"
      options={DIAGNOSIS_OPTIONS}
      value={value}
      onChange={selectedOptions => {
        if (typeof onChange === 'function') {
          onChange(selectedOptions as SelectOption[])
        }
      }}
      isMulti
      hideSelectedOptions={false}
      placeholder={placeholder || 'Select...'}
      styles={{
        menu: base => {
          if (isMobile) {
            return {
              ...base,
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              maxHeight: '200px',
              borderRadius: '0.75rem',
              border: '1.6px solid #dfe6f2',
              background: 'white',
              boxShadow: '0px 8px 30px 0px rgba(0, 0, 0, 0.06)',
            }
          }
          return {
            ...base,
            borderRadius: '0.75rem',
            border: '1.6px solid #dfe6f2',
            background: 'white',
            boxShadow: '0px 8px 30px 0px rgba(0, 0, 0, 0.06)',
          }
        },
        menuList: base => {
          if (isMobile) {
            return {
              ...base,
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              maxHeight: '200px',
            }
          }
          return {
            ...base,
            paddingTop: 0,
            borderRadius: '0.75rem',
          }
        },
        option: (base, state) => ({
          ...base,
          padding: '0.5rem 0.75rem',
          cursor: 'pointer',
          color: '#1a1a1a',
          fontSize: '1rem',
          fontWeight: 600,
          backgroundColor: state.isFocused
            ? '#f5f8fb'
            : state.isSelected
            ? '#f5f8fb'
            : 'transparent',
        }),
        group: base => ({
          ...base,
          padding: 0,
          margin: 0,
        }),
        groupHeading(base, props) {
          return {
            ...base,
            backgroundColor: '#E9EEF6',
            color: '#000',
            fontSize: '1rem',
            fontWeight: 600,
            padding: '0.5rem 1rem',
            margin: 0,
          }
        },
        ...styleOverrides,
      }}
      components={{
        Option: CustomOption,
        MultiValue: ({ data, getValue, ...props }) => {
          const values = getValue()

          // Mobile: show first 2 values
          if (isMobile && values.length > 2) {
            const index = values.findIndex(v => v.value === data.value)
            if (index >= 2) {
              return null
            }
            if (index === 1) {
              return (
                <components.MultiValue
                  {...props}
                  data={data}
                  getValue={getValue}>
                  {data.label}, ...
                </components.MultiValue>
              )
            }
          }

          // Tablet: show first 4 values
          if (isTablet && !isMobile && values.length > 4) {
            const index = values.findIndex(v => v.value === data.value)
            if (index >= 4) {
              return null
            }
            if (index === 3) {
              return (
                <components.MultiValue
                  {...props}
                  data={data}
                  getValue={getValue}>
                  {data.label}, ...
                </components.MultiValue>
              )
            }
          }

          return (
            <components.MultiValue {...props} data={data} getValue={getValue}>
              {data.label}
              {values[values.length - 1]?.value === data.value ? '' : ', '}
            </components.MultiValue>
          )
        },
        ...componentsOverrides,
      }}
      menuShouldBlockScroll={true}
      menuPosition="fixed"
      menuPortalTarget={
        typeof document !== 'undefined' ? document.body : undefined
      }
    />
  )
}

export default DiagnosisSelect
